// Ton der PIPR-Linie: die wahrscheinlichste Entscheidung des jeweiligen Tages.
//
// Für jeden Tag ist die Headline-Entscheidung das Bin mit der höchsten
// Wahrscheinlichkeit — dasselbe Objekt, das die Wolke darunter zeigt. Das
// Vorzeichen des zugehörigen Deltas gibt den Ton: Anhebung rot, Senkung blau,
// keine Änderung neutral.
//
// Zwei Feinheiten, die den Unterschied zwischen lesbar und Zebrastreifen machen:
//
// 1. `minShare` — bei einer 51/49-Lage kippt das Argmax von Tag zu Tag hin und
//    her. Liegt die Modalmasse unter der Schwelle, wird der Ton des Vortags
//    gehalten statt zu flackern. Eine Darstellungsentscheidung, keine
//    Datenänderung: die Zahlen im Tooltip bleiben unberührt.
//
// 2. Läufe teilen ihren Übergangspunkt. Ein Lauf endet auf demselben Index, auf
//    dem der nächste beginnt — sonst klafft an jedem Farbwechsel eine Lücke von
//    einem Tag.

import { PALETTE, binColor } from "../../palette";

/** Ton = Vorzeichen der wahrscheinlichsten Entscheidung. */
export type Tone = "hike" | "cut" | "hold";

export interface Toned {
    tone: Tone;
    /** Delta des Modus in bp — für die Farbwahl über binColor */
    delta: number;
}

/**
 * Ton je Tag.
 *
 * @param deltas   globales Delta-Raster in bp, aufsteigend (model.deltas)
 * @param p        Permille je Tag über das Raster (d.p aus model.vis)
 * @param minShare Mindestanteil des Modus (0..1), darunter wird gehalten
 */
export function modalTones(deltas: number[], p: number[][], minShare = 0.5): Toned[] {
    const floor = Math.round(minShare * 1000);
    const out: Toned[] = new Array(p.length);
    let held: Toned = { tone: "hold", delta: 0 };

    for (let i = 0; i < p.length; i++) {
        const row = p[i];
        let best = 0;
        for (let j = 1; j < row.length; j++) if (row[j] > row[best]) best = j;

        if (row[best] < floor && i > 0) {
            out[i] = held;
            continue;
        }
        const d = deltas[best];
        held = { tone: d > 0 ? "hike" : d < 0 ? "cut" : "hold", delta: d };
        out[i] = held;
    }
    return out;
}

export interface ToneRun {
    /** Index des ersten Punkts, inklusive */
    from: number;
    /** Index des letzten Punkts — zugleich `from` des nächsten Laufs */
    to: number;
    tone: Tone;
    delta: number;
}

/** Zusammenhängende Läufe gleichen Tons, mit geteiltem Übergangspunkt. */
export function toneRuns(toned: Toned[]): ToneRun[] {
    if (!toned.length) return [];
    const runs: ToneRun[] = [];
    let start = 0;

    for (let i = 1; i <= toned.length; i++) {
        if (i === toned.length || toned[i].tone !== toned[start].tone) {
            runs.push({
                from: start,
                to: i - 1,
                tone: toned[start].tone,
                delta: toned[start].delta,
            });
            start = i;
        }
    }
    // Übergangspunkt teilen, damit die Linie durchgezogen bleibt.
    for (let k = 0; k < runs.length - 1; k++) runs[k].to = runs[k + 1].from;
    return runs;
}

export interface GradientStop {
    /** 0..1 entlang der x-Achse */
    offset: number;
    color: string;
}

/**
 * Läufe als harte Gradient-Stops.
 *
 * Ein einziger Pfad mit `stroke="url(#id)"` statt n Pfaden: Strichstärke,
 * Linecap und das Hover-Handling bleiben an einem Element, und es gibt keine
 * Naht an den Übergängen. Harte Kante = zwei Stops auf derselben Position.
 *
 * @param xAt     Pixel-x des Punkts i
 * @param colorOf Farbe eines Laufs — üblicherweise über binColor, damit Linie
 *                und Wolke dieselbe Sprache sprechen
 */
export function toneGradientStops(
    runs: ToneRun[],
    xAt: (i: number) => number,
    colorOf: (run: ToneRun) => string,
): GradientStop[] {
    if (!runs.length) return [];
    const x0 = xAt(runs[0].from);
    const x1 = xAt(runs[runs.length - 1].to);
    const span = x1 - x0;
    if (span <= 0) return [];

    const at = (px: number) => Math.min(1, Math.max(0, (px - x0) / span));
    const stops: GradientStop[] = [];

    for (const run of runs) {
        const color = colorOf(run);
        stops.push({ offset: at(xAt(run.from)), color });
        stops.push({ offset: at(xAt(run.to)), color });
    }
    return stops;
}

/**
 * Farbe einer Entscheidung — die eine Stelle, an der Linie, Wolke und Kopfzeile
 * sich einigen. Anhebung und Senkung folgen der Wolkenskala, Stillstand bleibt
 * Tinte: eine eigene Farbe für "nichts passiert" wäre eine Aussage zu viel.
 */
export function toneColor(delta: number): string {
    return delta === 0 ? PALETTE.ink : binColor(delta);
}